/**
 * Helpers for linking into the partner portal from either React app.
 *
 * In production the partner portal lives on partners.padpulse.io while the main
 * marketing site is padpulse.io. In dev/staging both apps share one origin and
 * the portal is selected by the `?portal=partner` query param (see
 * server/vite.ts). These helpers centralize that branching so no page hardcodes
 * the host or forgets the dev query param (which silently 404s on the main app).
 */

function isMainAppProduction(): boolean {
  return typeof window !== "undefined" && window.location.hostname === "padpulse.io";
}

/**
 * Build a URL that lands inside the partner portal for a given path.
 *
 * - On the production main app (padpulse.io) → absolute https://partners.padpulse.io{path}
 * - Everywhere else (dev, staging, the portal itself) → {path}?portal=partner so
 *   the dev portal-selection gate routes it to the partner app.
 */
export function partnerPortalPath(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (isMainAppProduction()) {
    return `https://partners.padpulse.io${normalized}`;
  }
  const separator = normalized.includes("?") ? "&" : "?";
  return `${normalized}${separator}portal=partner`;
}

/**
 * Partner portal root URL — the program landing / "already a partner?" entry.
 */
export function partnerPortalUrl(): string {
  return partnerPortalPath("/");
}

/**
 * Partner portal sign-in role chooser URL.
 */
export function partnerPortalLoginUrl(): string {
  return partnerPortalPath("/login");
}

/**
 * Apply URL for a given screened partner type (lender, wholesaler, realtor,
 * broker). Task #3495 — these now point at the screening intake (`/join/:type`),
 * NOT the agreement. The applicant signs the agreement only after a soft approve.
 * Affiliate / founder_circle invite paths still link straight to `/apply/:type`.
 */
export function partnerPortalApplyUrl(type: string): string {
  return partnerPortalPath(`/join/${type}`);
}

/**
 * Map a partner's `partnerType` to the base path of its OWN portal shell.
 *
 * This is the single source of truth shared by partner-main.tsx (the top-level
 * router that picks the default landing route) and every portal shell's
 * wrong-partnerType guard. Keeping the mapping here means the six partner types
 * can never drift between those call sites. In particular an affiliate or
 * founder_circle partner that reaches a lender/broker/realtor/wholesaler base
 * (e.g. by typing the URL) is sent to `/affiliates` / `/founders` instead of
 * being bounced to a foreign shell — which would itself re-redirect and loop.
 */
export function partnerPortalBase(partnerType: string): string {
  switch (partnerType) {
    case "lender":
      return "/lenders";
    case "wholesaler":
      return "/wholesalers";
    case "broker":
      return "/brokers";
    case "realtor":
      return "/realtors";
    case "affiliate":
      return "/affiliates";
    case "founder_circle":
      return "/founders";
    case "property_manager":
      return "/property-managers";
    default:
      return "/lenders";
  }
}
